import React, { useState, useEffect } from 'react';
import { AlertTriangle, Tag, MapPin, AlignLeft } from 'lucide-react';
import FormModalLayout from '../Common/FormModalLayout';
import FormSection from '../Common/FormSection';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { ensureRichTextValue, isEmptyRichText, RichTextEditor } from '../RichTextEditor';
import MapLocationPicker from '../Map/MapLocationPicker';
import ComplaintPhotoSection from './ComplaintPhotoSection';

const STATUS_OPTIONS = [
  { value: 1, label: 'Nova' },
  { value: 2, label: 'Em análise' },
  { value: 3, label: 'Atendida' },
  { value: 4, label: 'Encaminhada' },
  { value: 5, label: 'Improcedente' },
  { value: 6, label: 'Não Localizada' },
  { value: 7, label: 'Em Monitoramento' },
  { value: 8, label: 'Auto de Infração' },
  { value: 9, label: 'Arquivada' },
  { value: 10, label: 'Enviada p/ Arquivamento' },
  { value: 11, label: 'Enviada p/ Jurídico' },
  { value: 12, label: 'Enviada p/ Gabinete' },
  { value: 13, label: 'Enviada p/ Licenciamento' },
  { value: 14, label: 'Notificação' },
  { value: 15, label: 'Interditada' },
  { value: 16, label: 'Embargada' },
];

const ComplaintModal = ({
  complaint,
  categories = [],
  enterprises = [],
  licenses = [],
  servidores = [],
  onSubmit,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    estado: '',
    cidade: '',
    bairro: '',
    rua: '',
    local: '',
    ponto_referencia: '',
    descricao: '',
    data: new Date().toISOString().slice(0, 16),
    category_id: '',
    enterprise_id: '',
    license_id: '',
    status: 1,
    prazo_atendimento: '',
    servidor_id: '',
    latitude: '',
    longitude: '',
  });
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    if (complaint) {
      setFormData({
        estado: complaint.estado || '',
        cidade: complaint.cidade || '',
        bairro: complaint.bairro || '',
        rua: complaint.rua || '',
        local: complaint.local || '',
        ponto_referencia: complaint.ponto_referencia || '',
        descricao: ensureRichTextValue(complaint.descricao || ''),
        data: complaint.data ? complaint.data.slice(0, 16) : new Date().toISOString().slice(0, 16),
        category_id: complaint.category_id || '',
        enterprise_id: complaint.enterprise_id || '',
        license_id: complaint.license_id || '',
        status: complaint.status ?? 1,
        prazo_atendimento: complaint.prazo_atendimento ? String(complaint.prazo_atendimento).slice(0, 10) : '',
        servidor_id: complaint.servidor_id || '',
        latitude: complaint.latitude ?? '',
        longitude: complaint.longitude ?? '',
      });
      setPendingFiles([]);
    } else {
      setFormData({
        estado: '',
        cidade: '',
        bairro: '',
        rua: '',
        local: '',
        ponto_referencia: '',
        descricao: '',
        data: new Date().toISOString().slice(0, 16),
        category_id: categories[0]?.id || '',
        enterprise_id: '',
        license_id: '',
        status: 1,
        prazo_atendimento: '',
        servidor_id: '',
        latitude: '',
        longitude: '',
      });
      setPendingFiles([]);
    }
  }, [complaint, categories]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      data: new Date(formData.data).toISOString(),
      category_id: formData.category_id || undefined,
      enterprise_id: formData.enterprise_id || undefined,
      license_id: formData.license_id || undefined,
      prazo_atendimento: formData.prazo_atendimento || undefined,
      servidor_id: formData.servidor_id || undefined,
      latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
      longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
      descricao: isEmptyRichText(formData.descricao) ? null : (formData.descricao || '').trim(),
    };
    onSubmit(payload, pendingFiles);
  };

  return (
    <FormModalLayout
      title={complaint ? 'Editar denúncia' : 'Nova denúncia'}
      subtitle="Registro de ocorrência ambiental com localização e classificação."
      headerIcon={AlertTriangle}
      onClose={onClose}
      size="2xl"
      historyEntityType="complaints"
      historyEntityId={complaint?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancelar
          </button>
          <Can permission={P.COMPLAINT_ACCESS}>
            <button type="submit" form="complaint-modal-form" className="btn btn-primary">
              {complaint ? 'Salvar' : 'Criar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="complaint-modal-form" onSubmit={handleSubmit} className="form-stack space-y-5">
        <FormSection
          id="complaint-classificacao"
          title="Classificação"
          description="Data, categoria e status do atendimento."
          icon={Tag}
          variant="primary"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="form-group">
              <label htmlFor="cmp-data" className="form-label form-label-required">
                Data
              </label>
              <input
                id="cmp-data"
                type="datetime-local"
                value={formData.data}
                onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-cat" className="form-label form-label-required">
                Categoria
              </label>
              <select
                id="cmp-cat"
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="form-select"
                required
              >
                <option value="">Selecione</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cmp-enterprise" className="form-label">
                Empreendimento
              </label>
              <select
                id="cmp-enterprise"
                value={formData.enterprise_id}
                onChange={(e) => setFormData({ ...formData, enterprise_id: e.target.value })}
                className="form-select"
              >
                <option value="">Sem vínculo</option>
                {enterprises.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name || e.nome || e.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cmp-license" className="form-label">
                Licença
              </label>
              <select
                id="cmp-license"
                value={formData.license_id}
                onChange={(e) => setFormData({ ...formData, license_id: e.target.value })}
                className="form-select"
              >
                <option value="">Sem vínculo</option>
                {licenses
                  .filter(
                    (l) =>
                      !formData.enterprise_id ||
                      String(l.empreendimento_id || '') === String(formData.enterprise_id)
                  )
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.numero || l.id}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cmp-prazo" className="form-label">
                Prazo de atendimento
              </label>
              <input
                id="cmp-prazo"
                type="date"
                value={formData.prazo_atendimento}
                onChange={(e) => setFormData({ ...formData, prazo_atendimento: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-servidor" className="form-label">
                Servidor responsável
              </label>
              <select
                id="cmp-servidor"
                value={formData.servidor_id}
                onChange={(e) => setFormData({ ...formData, servidor_id: e.target.value })}
                className="form-select"
              >
                <option value="">Sem responsável</option>
                {servidores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group md:col-span-2">
              <label htmlFor="cmp-status" className="form-label">
                Status
              </label>
              <select
                id="cmp-status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value, 10) })}
                className="form-select max-w-md"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </FormSection>

        <FormSection
          id="complaint-localizacao"
          title="Localização"
          description="Endereço e coordenadas, quando disponíveis."
          icon={MapPin}
          variant="slate"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="form-group">
              <label htmlFor="cmp-uf" className="form-label">
                Estado (UF)
              </label>
              <input
                id="cmp-uf"
                type="text"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="form-input"
                placeholder="Ex: AL"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-city" className="form-label">
                Cidade
              </label>
              <input
                id="cmp-city"
                type="text"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-bairro" className="form-label">
                Bairro
              </label>
              <input
                id="cmp-bairro"
                type="text"
                value={formData.bairro}
                onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-rua" className="form-label">
                Rua
              </label>
              <input
                id="cmp-rua"
                type="text"
                value={formData.rua}
                onChange={(e) => setFormData({ ...formData, rua: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group md:col-span-2">
              <label htmlFor="cmp-local" className="form-label">
                Endereço / local
              </label>
              <input
                id="cmp-local"
                type="text"
                value={formData.local}
                onChange={(e) => setFormData({ ...formData, local: e.target.value })}
                className="form-input"
                placeholder="Endereço completo ou ponto de referência"
              />
            </div>
            <div className="form-group md:col-span-2">
              <label htmlFor="cmp-ref" className="form-label">
                Ponto de referência
              </label>
              <input
                id="cmp-ref"
                type="text"
                value={formData.ponto_referencia}
                onChange={(e) => setFormData({ ...formData, ponto_referencia: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group md:col-span-2">
              <label className="form-label">Ponto no mapa</label>
              <MapLocationPicker
                latitude={formData.latitude === '' ? null : Number(formData.latitude)}
                longitude={formData.longitude === '' ? null : Number(formData.longitude)}
                onChange={({ latitude: la, longitude: lo }) =>
                  setFormData((f) => ({ ...f, latitude: String(la), longitude: String(lo) }))
                }
                height="240px"
                defaultCenter={[-35.7353, -9.6658]}
                defaultZoom={12}
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-lat" className="form-label">
                Latitude
              </label>
              <input
                id="cmp-lat"
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                className="form-input"
                placeholder="Ex: -9.6498"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cmp-lng" className="form-label">
                Longitude
              </label>
              <input
                id="cmp-lng"
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                className="form-input"
                placeholder="Ex: -35.7089"
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          id="complaint-descricao"
          title="Descrição"
          description="Relato detalhado da ocorrência."
          icon={AlignLeft}
          variant="amber"
        >
          <div className="form-group">
            <label htmlFor="cmp-desc" className="form-label">
              Texto da denúncia
            </label>
            <RichTextEditor
              value={formData.descricao}
              onChange={(v) => setFormData({ ...formData, descricao: v })}
              placeholder="Descrição da denúncia"
            />
          </div>
        </FormSection>

        <ComplaintPhotoSection
          complaintId={complaint?.id}
          pendingFiles={pendingFiles}
          onPendingFilesChange={setPendingFiles}
        />
      </form>
    </FormModalLayout>
  );
};

export default ComplaintModal;
